import React, { createContext, useEffect, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

import { DarkGlobalStyles, darkTheme, LightGlobalStyles, lightTheme } from "./themes";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { THEME } from "./types";

const THEME_KEY = "theme";

const crmThemes = {
  [THEME.light]: lightTheme,
  [THEME.dark]: darkTheme,
};

export const ThemeContext = createContext<{ theme: THEME; toggleTheme: () => void }>({
  theme: THEME.light,
  toggleTheme: () => {},
});

type CRMThemeProviderProps = {
  children: React.ReactNode;
};

export const CrmThemeProvider: React.FC<CRMThemeProviderProps> = ({ children }) => {
  const { setItem, getItem } = useLocalStorage();
  const [theme, setTheme] = useState<THEME>(THEME.light);

  const applyTheme = (t: THEME) => {
    setItem(THEME_KEY, t);
    setTheme(t);
  };

  // Restore the persisted choice once on mount. Read only — writing here would
  // re-run this effect through the storage hook's state.
  useEffect(() => {
    const stored = getItem(THEME_KEY);
    if (stored === THEME.dark || stored === THEME.light) {
      setTheme(stored);
    }
  }, [getItem]);

  const toggleTheme = () => {
    applyTheme(theme === THEME.light ? THEME.dark : THEME.light);
  };

  const GlobalStyles = theme === THEME.dark ? DarkGlobalStyles : LightGlobalStyles;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <StyledThemeProvider theme={crmThemes[theme]}>
        {children}
        <GlobalStyles />
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default lightTheme;
